/**
 * UserForm Component
 * 
 * Form component for creating and editing users.
 * Used in the User Management screen for managers.
 * 
 * @module UserForm
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  User,
  Mail,
  Phone,
  Building,
  Shield,
  UserCheck,
  X,
  Save,
  Eye,
  EyeOff,
  Store as StoreIcon,
  Users,
  Search,
  Lock
} from 'lucide-react';
import { UserProfile, CreateUserData, UpdateUserData, UserRole, UserStatus } from '../../@types/user';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';

/**
 * UserForm props interface
 */
export interface UserFormProps {
  /** User to edit (undefined for new user) */
  user?: UserProfile;
  
  /** Form mode */
  mode: 'create' | 'edit';
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Handle form submission */
  onSubmit: (data: CreateUserData | UpdateUserData) => Promise<void>;
  
  /** Handle form cancellation */
  onCancel: () => void;
  
  /** Available departments for selection */
  departments?: string[];
  
  /** Available sales groups for selection */
  salesGroups?: string[];
  
  /** Available stores for assignment */
  stores?: Array<{ id: string; name: string; customerNo: string | number; salesGroup?: string }>;
}

/**
 * Styled components
 */
const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.lg};
`;

const FormContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.xl};
  width: 100%;
  max-width: 80vw;
  max-height: 90vh;
  overflow-y: auto;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const FormTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.sm};
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FormContent = styled.div`
  padding: ${Spacing.lg};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const Label = styled.label`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const Required = styled.span`
  color: ${ThemeColors.error.main};
`;

const Input = styled.input`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light};
  }

  &:disabled {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.secondary};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const Select = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light};
  }

  &:disabled {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.secondary};
    cursor: not-allowed;
  }
`;

const MultiSelect = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light};
  }

  &:disabled {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.secondary};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  cursor: pointer;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: ${ThemeColors.primary.main};
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${Spacing.md};
  padding: ${Spacing.lg};
  border-top: 1px solid ${ThemeColors.border.light};
  margin-top: ${Spacing.lg};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.lg};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;
    
    &:hover:not(:disabled) {
      background: ${ThemeColors.primary.dark};
    }
    
    &:disabled {
      background: ${ThemeColors.gray[400]};
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.main};
    
    &:hover:not(:disabled) {
      background: ${ThemeColors.background.secondary};
      border-color: ${ThemeColors.primary.main};
    }
  `}

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ErrorMessage = styled.div`
  color: ${ThemeColors.error.main};
  font-size: ${Typography.fontSize.xs};
  margin-top: ${Spacing.xs};
`;

const PasswordInput = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${Spacing.sm};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  padding: ${Spacing.xs};

  &:hover {
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

/**
 * UserForm Component
 * 
 * @example
 * ```tsx
 * <UserForm
 *   mode="create"
 *   isLoading={isCreating}
 *   onSubmit={handleCreateUser}
 *   onCancel={handleCancel}
 *   territories={territories}
 *   regions={regions}
 * />
 * ```
 */
// Canadian Provinces
const CANADIAN_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon'
];

export const UserForm: React.FC<UserFormProps> = ({
  user,
  mode,
  isLoading = false,
  onSubmit,
  onCancel,
  departments = [],
  salesGroups = [],
  stores = []
}) => {
  const { t } = useTranslation();
  
  // Form state
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: 'sales-representative',
    status: 'active',
    department: '',
    sendWelcomeEmail: true,
    temporaryPassword: ''
  });
  
  // Sales groups state (for multi-select)
  const [selectedSalesGroups, setSelectedSalesGroups] = useState<string[]>([]);
  
  // Store assignment state
  const [assignedStoreIds, setAssignedStoreIds] = useState<string[]>([]);
  const [storeSearchFilter, setStoreSearchFilter] = useState<string>('');

  // Email and password state for edit mode
  const [email, setEmail] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber || '',
        role: user.role,
        department: user.department || '',
      } as UpdateUserData);
      
      // Initialize email for edit mode
      setEmail(user.email || '');
      
      // Initialize sales groups - prioritize salesGroups array over single salesGroup
      if ((user as any).salesGroups && Array.isArray((user as any).salesGroups) && (user as any).salesGroups.length > 0) {
        // If it's already an array with values, use it
        setSelectedSalesGroups((user as any).salesGroups);
      } else if (user.salesGroup) {
        // Fallback to single salesGroup string, convert to array
        setSelectedSalesGroups([user.salesGroup]);
      } else {
        setSelectedSalesGroups([]);
      }
      
      // Initialize assigned stores - prefer existing assignments, otherwise auto-match by SalesGroup
      if (user.assignedStoreIds && Array.isArray(user.assignedStoreIds) && user.assignedStoreIds.length > 0) {
        // Use existing assignments
        setAssignedStoreIds(user.assignedStoreIds);
      } else {
        // Auto-match stores by SalesGroup if user has sales groups
        const userSalesGroups = user.salesGroup 
          ? [user.salesGroup.toUpperCase()]
          : ((user as any).salesGroups || []).map((sg: string) => sg.toUpperCase());
        
        if (userSalesGroups.length > 0 && stores.length > 0) {
          const matchingStoreIds = stores
            .filter(store => {
              const storeSalesGroup = (store.salesGroup || '').toUpperCase();
              return userSalesGroups.includes(storeSalesGroup);
            })
            .map(store => store.id);
          setAssignedStoreIds(matchingStoreIds);
        } else {
          setAssignedStoreIds([]);
        }
      }
    } else {
      // Reset for new user
      setSelectedSalesGroups([]);
    }
  }, [user, mode, stores]);

  /**
   * Handle input changes
   */
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (mode === 'create') {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else if (mode === 'edit') {
      // Validate email in edit mode
      if (!email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (mode === 'create' && formData.temporaryPassword && formData.temporaryPassword.length < 8) {
      newErrors.temporaryPassword = 'Password must be at least 8 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Include assigned store IDs and sales groups in the submission data
      // Always set status to 'active' since all users are active
      const submissionData: CreateUserData | UpdateUserData = {
        ...formData,
        status: 'active', // All users are always active
        salesGroups: selectedSalesGroups.length > 0 ? selectedSalesGroups : undefined,
        salesGroup: selectedSalesGroups.length === 1 ? selectedSalesGroups[0] : undefined, // For backward compatibility
        assignedStoreIds: mode === 'edit' ? assignedStoreIds : undefined
      };
      
      // Add email and password for edit mode if provided
      if (mode === 'edit') {
        if (email && email !== user?.email) {
          (submissionData as UpdateUserData).email = email;
        }
        if (newPassword && newPassword.trim().length > 0) {
          if (newPassword.length < 8) {
            setErrors(prev => ({ ...prev, newPassword: 'Password must be at least 8 characters long' }));
            return;
          }
          (submissionData as UpdateUserData).password = newPassword;
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserForm.tsx:569',message:'Password added to submission data',data:{hasPassword:true,passwordLength:newPassword.length,userId:user?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
          // #endregion
        } else {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserForm.tsx:571',message:'No password provided in edit mode',data:{hasPassword:false,newPasswordValue:newPassword,userId:user?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
          // #endregion
        }
      }
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserForm.tsx:580',message:'Submitting form data',data:{mode,hasPassword:!!(submissionData as UpdateUserData).password,passwordLength:(submissionData as UpdateUserData).password?.length,userId:user?.id,submissionKeys:Object.keys(submissionData),submissionDataPasswordField:(submissionData as UpdateUserData).password?((submissionData as UpdateUserData).password.substring(0,2)+'***'):null},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      await onSubmit(submissionData);
      
      // Show success message
      if (mode === 'edit') {
        toast.success('User updated successfully!');
      } else {
        toast.success('User created successfully!');
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      toast.error(error.message || 'Failed to save changes. Please try again.');
    }
  };
  
  /**
   * Handle sales group selection (checkboxes)
   */
  const handleSalesGroupToggle = (salesGroup: string) => {
    setSelectedSalesGroups(prev => {
      if (prev.includes(salesGroup)) {
        return prev.filter(sg => sg !== salesGroup);
      } else {
        return [...prev, salesGroup];
      }
    });
  };

  /**
   * Toggle store assignment
   */
  const handleStoreToggle = (storeId: string) => {
    setAssignedStoreIds(prev => {
      if (prev.includes(storeId)) {
        return prev.filter(id => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });
  };

  /**
   * Handle overlay click
   */
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <FormOverlay onClick={handleOverlayClick}>
      <FormContainer>
        <FormHeader>
          <FormTitle>
            {mode === 'create' ? (
              <>
                <User />
                Create New User
              </>
            ) : (
              <>
                <User />
                Edit User
              </>
            )}
          </FormTitle>
          <CloseButton onClick={onCancel}>
            <X />
          </CloseButton>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormContent>
            <FormGrid>
              {/* Personal Information */}
              <FormSection>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: Typography.fontSize.lg, 
                  fontWeight: Typography.fontWeight.semibold,
                  color: ThemeColors.text.primary 
                }}>
                  Personal Information
                </h3>
                
                {(mode === 'create' || mode === 'edit') && (
                  <FormGroup>
                    <Label htmlFor="email">
                      <Mail />
                      Email Address <Required>*</Required>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={mode === 'edit' ? email : formData.email}
                      onChange={(e) => {
                        if (mode === 'edit') {
                          setEmail(e.target.value);
                        } else {
                          handleInputChange('email', e.target.value);
                        }
                      }}
                      placeholder="Enter email address"
                      disabled={isLoading}
                    />
                    {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                  </FormGroup>
                )}

                <FormGroup>
                  <Label htmlFor="firstName">
                    <User />
                    First Name <Required>*</Required>
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                    disabled={isLoading}
                  />
                  {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="lastName">
                    <User />
                    Last Name <Required>*</Required>
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                    disabled={isLoading}
                  />
                  {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="phoneNumber">
                    <Phone />
                    Phone Number
                  </Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    placeholder="Enter phone number"
                    disabled={isLoading}
                  />
                </FormGroup>
              </FormSection>

              {/* Role */}
              <FormSection>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: Typography.fontSize.lg, 
                  fontWeight: Typography.fontWeight.semibold,
                  color: ThemeColors.text.primary 
                }}>
                  Role
                </h3>

                <FormGroup>
                  <Label htmlFor="role">
                    <Shield />
                    Role <Required>*</Required>
                  </Label>
                  <Select
                    id="role"
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value as UserRole)}
                    disabled={isLoading}
                  >
                    <option value="sales-representative">Sales Representative</option>
                    <option value="manager">Manager</option>
                    <option value="hand-lead">Hand Lead</option>
                  </Select>
                </FormGroup>


                {mode === 'create' && (
                  <FormGroup>
                    <Label htmlFor="temporaryPassword">
                      <Shield />
                      Temporary Password
                    </Label>
                    <PasswordInput>
                      <Input
                        id="temporaryPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.temporaryPassword || ''}
                        onChange={(e) => handleInputChange('temporaryPassword', e.target.value)}
                        placeholder="Enter temporary password (optional)"
                        disabled={isLoading}
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff /> : <Eye />}
                      </PasswordToggle>
                    </PasswordInput>
                    {errors.temporaryPassword && <ErrorMessage>{errors.temporaryPassword}</ErrorMessage>}
                  </FormGroup>
                )}

                {mode === 'edit' && (
                  <FormGroup>
                    <Label htmlFor="newPassword">
                      <Lock />
                      New Password
                    </Label>
                    <PasswordInput>
                      <Input
                        id="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          // #region agent log
                          fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserForm.tsx:797',message:'Password field changed',data:{passwordLength:e.target.value.length,hasValue:!!e.target.value},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                          // #endregion
                          if (errors.newPassword) {
                            setErrors(prev => ({ ...prev, newPassword: '' }));
                          }
                        }}
                        placeholder="Enter new password (leave blank to keep current)"
                        disabled={isLoading}
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <EyeOff /> : <Eye />}
                      </PasswordToggle>
                    </PasswordInput>
                    {errors.newPassword && <ErrorMessage>{errors.newPassword}</ErrorMessage>}
                    <div style={{ 
                      marginTop: Spacing.xs, 
                      fontSize: Typography.fontSize.xs, 
                      color: ThemeColors.text.secondary 
                    }}>
                      Leave blank to keep current password
                    </div>
                  </FormGroup>
                )}
              </FormSection>

              {/* Organization */}
              <FormSection>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: Typography.fontSize.lg, 
                  fontWeight: Typography.fontWeight.semibold,
                  color: ThemeColors.text.primary 
                }}>
                  Organization
                </h3>

                <FormGroup>
                  <Label htmlFor="department">
                    <Building />
                    Department
                  </Label>
                  <Select
                    id="department"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">Select Department</option>
                    {departments.map(department => (
                      <option key={department} value={department}>{department}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>
                    <Users />
                    Sales Group Associated
                  </Label>
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: `1px solid ${ThemeColors.border.main}`,
                    borderRadius: BorderRadius.sm,
                    padding: Spacing.sm
                  }}>
                    <CheckboxGroup>
                      {salesGroups.map(salesGroup => {
                        const isChecked = selectedSalesGroups.includes(salesGroup);
                        return (
                          <CheckboxItem key={salesGroup}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleSalesGroupToggle(salesGroup)}
                              disabled={isLoading}
                            />
                            <span>{salesGroup}</span>
                          </CheckboxItem>
                        );
                      })}
                    </CheckboxGroup>
                  </div>
                  <div style={{ 
                    marginTop: Spacing.xs, 
                    fontSize: Typography.fontSize.xs, 
                    color: ThemeColors.text.secondary 
                  }}>
                    {selectedSalesGroups.length > 0 
                      ? `${selectedSalesGroups.length} sales group${selectedSalesGroups.length !== 1 ? 's' : ''} selected: ${selectedSalesGroups.join(', ')}`
                      : 'Select one or more sales groups'
                    }
                  </div>
                </FormGroup>
              </FormSection>

              {/* Store Assignment */}
              {mode === 'edit' && stores.length > 0 && (() => {
                // Filter stores based on search
                const filteredStores = stores.filter(store => {
                  if (!storeSearchFilter.trim()) return true;
                  const searchLower = storeSearchFilter.toLowerCase();
                  const storeName = (store.name || '').toLowerCase();
                  const customerNo = String(store.customerNo || '').toLowerCase();
                  return storeName.includes(searchLower) || customerNo.includes(searchLower);
                });

                // Sort stores: assigned first, then unassigned, then alphabetically
                const sortedStores = [...filteredStores].sort((a, b) => {
                  const aAssigned = assignedStoreIds.includes(a.id);
                  const bAssigned = assignedStoreIds.includes(b.id);
                  
                  // Assigned stores come first
                  if (aAssigned && !bAssigned) return -1;
                  if (!aAssigned && bAssigned) return 1;
                  
                  // Within assigned/unassigned groups, sort by customer number, then name
                  const aCustomerNo = String(a.customerNo || '').padStart(10, '0');
                  const bCustomerNo = String(b.customerNo || '').padStart(10, '0');
                  if (aCustomerNo !== bCustomerNo) {
                    return aCustomerNo.localeCompare(bCustomerNo);
                  }
                  return (a.name || '').localeCompare(b.name || '');
                });

                // Count matched stores
                const userSalesGroups = selectedSalesGroups.map(sg => sg.toUpperCase());
                const matchedCount = stores.filter(s => {
                  const storeSalesGroup = (s.salesGroup || '').toUpperCase();
                  return userSalesGroups.length > 0 && storeSalesGroup && userSalesGroups.includes(storeSalesGroup);
                }).length;

                return (
                  <FormSection>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: Typography.fontSize.lg, 
                      fontWeight: Typography.fontWeight.semibold,
                      color: ThemeColors.text.primary 
                    }}>
                      <StoreIcon />
                      Store Assignment
                    </h3>
                    
                    <FormGroup>
                      <Label>
                        Assign Stores to User
                        {selectedSalesGroups.length > 0 && (
                          <span style={{ 
                            marginLeft: Spacing.xs, 
                            fontSize: Typography.fontSize.xs,
                            fontWeight: Typography.fontWeight.normal,
                            color: ThemeColors.text.secondary
                          }}>
                            (Auto-matched by SalesGroup: {selectedSalesGroups.join(', ')})
                          </span>
                        )}
                      </Label>
                      
                      {/* Search/Filter Input */}
                      <div style={{ position: 'relative', marginBottom: Spacing.sm }}>
                        <Search style={{
                          position: 'absolute',
                          left: Spacing.sm,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '16px',
                          height: '16px',
                          color: ThemeColors.text.secondary
                        }} />
                        <Input
                          type="text"
                          value={storeSearchFilter}
                          onChange={(e) => setStoreSearchFilter(e.target.value)}
                          placeholder="Search by store name or customer number..."
                          disabled={isLoading}
                          style={{
                            paddingLeft: `${Spacing.xl}`,
                            width: '100%'
                          }}
                        />
                      </div>

                      <div style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        border: `1px solid ${ThemeColors.border.main}`,
                        borderRadius: BorderRadius.sm,
                        padding: Spacing.sm
                      }}>
                        {sortedStores.length > 0 ? (
                          <CheckboxGroup>
                            {sortedStores.map(store => {
                              // Check if store matches any of the selected sales groups
                              const storeSalesGroup = (store.salesGroup || '').toUpperCase();
                              const isMatchedBySalesGroup = userSalesGroups.length > 0 && storeSalesGroup && userSalesGroups.includes(storeSalesGroup);
                              const isChecked = assignedStoreIds.includes(store.id);
                              
                              return (
                                <CheckboxItem key={store.id}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleStoreToggle(store.id)}
                                    disabled={isLoading}
                                  />
                                  <span style={{
                                    color: isMatchedBySalesGroup ? ThemeColors.primary.main : ThemeColors.text.primary,
                                    fontWeight: isChecked ? Typography.fontWeight.semibold : Typography.fontWeight.normal
                                  }}>
                                    {store.customerNo ? `${store.customerNo} (${store.name})` : store.name}
                                    {isMatchedBySalesGroup && (
                                      <span style={{
                                        marginLeft: Spacing.xs,
                                        fontSize: Typography.fontSize.xs,
                                        color: ThemeColors.success.main
                                      }}>
                                        (Matches SalesGroup: {store.salesGroup})
                                      </span>
                                    )}
                                  </span>
                                </CheckboxItem>
                              );
                            })}
                          </CheckboxGroup>
                        ) : (
                          <div style={{ 
                            padding: Spacing.md, 
                            textAlign: 'center', 
                            color: ThemeColors.text.secondary 
                          }}>
                            {storeSearchFilter.trim() ? 'No stores match your search' : 'No stores available'}
                          </div>
                        )}
                      </div>
                      <div style={{ 
                        marginTop: Spacing.xs, 
                        fontSize: Typography.fontSize.xs, 
                        color: ThemeColors.text.secondary 
                      }}>
                        {assignedStoreIds.length} store{assignedStoreIds.length !== 1 ? 's' : ''} assigned
                        {selectedSalesGroups.length > 0 && matchedCount > 0 && (
                          <span style={{ marginLeft: Spacing.sm, color: ThemeColors.primary.main }}>
                            ({matchedCount} matched by SalesGroup)
                          </span>
                        )}
                        {storeSearchFilter.trim() && (
                          <span style={{ marginLeft: Spacing.sm, color: ThemeColors.text.secondary }}>
                            ({sortedStores.length} shown)
                          </span>
                        )}
                      </div>
                    </FormGroup>
                  </FormSection>
                );
              })()}

              {/* Additional Options */}
              {mode === 'create' && (
                <FormSection>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: Typography.fontSize.lg, 
                    fontWeight: Typography.fontWeight.semibold,
                    color: ThemeColors.text.primary 
                  }}>
                    Additional Options
                  </h3>

                  <CheckboxGroup>
                    <CheckboxItem>
                      <input
                        type="checkbox"
                        checked={formData.sendWelcomeEmail}
                        onChange={(e) => handleInputChange('sendWelcomeEmail', e.target.checked)}
                        disabled={isLoading}
                      />
                      Send welcome email to user
                    </CheckboxItem>
                  </CheckboxGroup>
                </FormSection>
              )}
            </FormGrid>
          </FormContent>

          <FormActions>
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              <X />
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
            >
              <Save />
              {mode === 'create' ? 'Create User' : 'Save Changes'}
            </Button>
          </FormActions>
        </form>
      </FormContainer>
    </FormOverlay>
  );
};
